import { useState } from 'react';
import { Card, CardHeader } from '@etherisc/ui-kit';
import { CreditCard, TrendingUp, DollarSign, BarChart3 } from 'lucide-react';
import { BatchReconciliationView } from '../../components/insurance/BatchReconciliationView';
import { PremiumEarningManagement } from '../../components/insurance/PremiumEarningManagement';
import { CommissionTrackingView } from '../../components/insurance/CommissionTrackingView';

type TabType = 'batches' | 'earning' | 'commissions';

export const BatchManagement = () => {
  const [activeTab, setActiveTab] = useState<TabType>('batches');

  const tabs = [
    {
      id: 'batches' as TabType,
      label: 'Payment Batches',
      icon: CreditCard,
      description: 'Manage payment batch reconciliation'
    },
    {
      id: 'earning' as TabType,
      label: 'Premium Earning',
      icon: TrendingUp,
      description: 'Process premium earning for active policies'
    },
    {
      id: 'commissions' as TabType,
      label: 'Commission Tracking',
      icon: DollarSign,
      description: 'Track agent commissions and payments'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'batches':
        return <BatchReconciliationView />;
      case 'earning':
        return <PremiumEarningManagement />;
      case 'commissions':
        return <CommissionTrackingView />;
      default:
        return <BatchReconciliationView />;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <BarChart3 className="h-8 w-8" />
          Batch Management
        </h1>
        <p className="text-muted-foreground">
          Manage payment batches, premium earning, and commission tracking
        </p>
      </div>

      {/* Tab Navigation */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background hover:bg-muted border-border'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-2">
            <p className="text-sm text-muted-foreground">
              {tabs.find(tab => tab.id === activeTab)?.description}
            </p>
          </div>
        </CardHeader>
      </Card>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};
